<script setup lang="ts">
/**
 * Findings panel — the right-side panel of the narrative detail page, opened by
 * the top-right toggle in the workspace (see
 * docs_for_claude/figma-designs/latest/narrative-citationslist.png). Lists the
 * matter's findings (real data from findingsStore) with the mockup's chrome:
 * a "Cited in this narrative / All findings" scope dropdown, filter + sort
 * buttons, and per-row tag pills and a linked-reference count.
 *
 * Fidelity note: the finding list and the "Cited in this narrative" scope are
 * real — "cited" is backed by the open narrative's finding links
 * (narrativesStore.current.cited_finding_ids, derived from the finding anchors
 * embedded in each page's content). The tag pills and the per-row reference
 * count remain **visual mocks** — the findings backend has no tags and no
 * per-finding reference count yet. Swap those for real data when they land.
 */
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFindingsStore, type Finding } from '../../stores/findingsStore';
import { useNarrativesStore } from '../../stores/narrativesStore';

const props = defineProps<{
    // When set (e.g. a finding link was clicked in the editor), reveal and
    // briefly highlight this finding's row.
    highlightFindingId?: string | null;
}>();

const emit = defineEmits<{ (e: 'close'): void }>();

const { t } = useI18n();
const findingsStore = useFindingsStore();
const narrativesStore = useNarrativesStore();

// ── Scope dropdown ─────────────────────────────────────────────────────────
type Scope = 'cited' | 'all';
const scope = ref<Scope>('cited');
const scopeMenuOpen = ref(false);

// Findings cited by the open narrative (rollup across its pages). Real data,
// kept in sync by the store as page content is saved.
const citedIds = computed(() => new Set(narrativesStore.current?.cited_finding_ids ?? []));

const allCount = computed(() => findingsStore.findings.length);
const citedCount = computed(
    () => findingsStore.findings.filter((f) => citedIds.value.has(f.id)).length,
);

const scopeCount = (s: Scope) => (s === 'all' ? allCount.value : citedCount.value);

const toggleScopeMenu = () => {
    scopeMenuOpen.value = !scopeMenuOpen.value;
};

const selectScope = (s: Scope) => {
    scope.value = s;
    scopeMenuOpen.value = false;
};

// "Cited" shows only findings linked from this narrative's content; "All"
// shows the matter's full findings list.
const rows = computed(() =>
    scope.value === 'all'
        ? findingsStore.findings
        : findingsStore.findings.filter((f) => citedIds.value.has(f.id)),
);

// ── Mocked per-row extras ─────────────────────────────────────────────────
// A fixed set of demo tags so every row shows the mockup's pill row. These are
// placeholders — the findings backend carries no tags.
interface MockTag {
    label: string;
    color: string;
}
const MOCK_TAGS: MockTag[] = [
    { label: 'Safety', color: '#3aa757' },
    { label: 'Adverse', color: '#2f6fed' },
    { label: 'Vulnerable Populations', color: '#e05fb0' },
    { label: 'Regulation', color: '#8a5a2b' },
];

// A stable pseudo-count per finding so the "17" doesn't flicker between
// renders. Derived from the id, purely cosmetic.
const mockRefCount = (finding: Finding): number => {
    let hash = 0;
    for (const ch of finding.id) hash = (hash * 31 + ch.charCodeAt(0)) & 0xffff;
    return 5 + (hash % 40);
};

const rowTitle = (finding: Finding): string => finding.description;

// ── Reveal-on-click (finding link → highlight its row) ─────────────────────
const listEl = ref<HTMLElement | null>(null);
// The row that just flashed. Cleared after the animation so it can re-trigger.
const flashedId = ref<string | null>(null);
let flashTimer: ReturnType<typeof setTimeout> | undefined;

watch(
    () => props.highlightFindingId,
    async (id) => {
        if (!id) return;
        // Make sure the target row is in the current scope; if the "cited"
        // scope doesn't include it (e.g. links not yet saved), widen to "all".
        if (scope.value === 'cited' && !citedIds.value.has(id)) scope.value = 'all';
        await nextTick();
        const row = listEl.value?.querySelector<HTMLElement>(`[data-finding-row="${id}"]`);
        row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        flashedId.value = id;
        clearTimeout(flashTimer);
        flashTimer = setTimeout(() => {
            flashedId.value = null;
        }, 1600);
    },
);
</script>

<template>
    <aside class="narrative-findings-panel" :aria-label="t('narratives.findingsPanel.title')">
        <header class="nfp-header">
            <h2 class="nfp-title">{{ t('narratives.findingsPanel.title') }}</h2>
        </header>

        <div class="nfp-toolbar">
            <div class="nfp-scope">
                <button
                    type="button"
                    class="nfp-scope-trigger"
                    :aria-expanded="scopeMenuOpen"
                    @click="toggleScopeMenu"
                >
                    <span>{{ t(`narratives.findingsPanel.scope.${scope}`) }}</span>
                    <span class="nfp-scope-count">{{ scopeCount(scope) }}</span>
                    <span class="material-icons nfp-scope-caret">expand_more</span>
                </button>

                <div v-if="scopeMenuOpen" class="nfp-scope-menu" @click.stop>
                    <button type="button" class="nfp-scope-option" @click="selectScope('cited')">
                        <span class="material-icons nfp-scope-check">{{
                            scope === 'cited' ? 'check' : ''
                        }}</span>
                        <span class="nfp-scope-option-label">{{
                            t('narratives.findingsPanel.scope.cited')
                        }}</span>
                        <span class="nfp-scope-count">{{ citedCount }}</span>
                    </button>
                    <button type="button" class="nfp-scope-option" @click="selectScope('all')">
                        <span class="material-icons nfp-scope-check">{{
                            scope === 'all' ? 'check' : ''
                        }}</span>
                        <span class="nfp-scope-option-label">{{
                            t('narratives.findingsPanel.scope.all')
                        }}</span>
                        <span class="nfp-scope-count">{{ allCount }}</span>
                    </button>
                </div>
                <div
                    v-if="scopeMenuOpen"
                    class="nfp-scope-backdrop"
                    @click="scopeMenuOpen = false"
                ></div>
            </div>

            <div class="nfp-toolbar-actions">
                <button
                    type="button"
                    class="nfp-icon-btn"
                    :title="t('narratives.findingsPanel.filter')"
                    :aria-label="t('narratives.findingsPanel.filter')"
                >
                    <span class="material-icons-outlined">filter_list</span>
                </button>
                <button
                    type="button"
                    class="nfp-icon-btn"
                    :title="t('narratives.findingsPanel.sort')"
                    :aria-label="t('narratives.findingsPanel.sort')"
                >
                    <span class="material-icons-outlined">swap_vert</span>
                </button>
                <button
                    type="button"
                    class="nfp-icon-btn"
                    :title="t('common.close')"
                    :aria-label="t('common.close')"
                    @click="emit('close')"
                >
                    <span class="material-icons">close</span>
                </button>
            </div>
        </div>

        <div class="nfp-body">
            <!-- Loading -->
            <div
                v-if="findingsStore.loading && findingsStore.findings.length === 0"
                class="findings-status"
            >
                <div class="loading-dots"><span></span><span></span><span></span></div>
                <span>{{ t('narratives.findings.loading') }}</span>
            </div>

            <!-- Error -->
            <div v-else-if="findingsStore.error" class="findings-status findings-status-error">
                <span class="material-icons-outlined">error_outline</span>
                <span>{{ t('narratives.findings.error') }}</span>
            </div>

            <!-- Empty -->
            <div v-else-if="rows.length === 0" class="nfp-empty">
                {{ t('narratives.findingsPanel.empty') }}
            </div>

            <!-- List -->
            <ul v-else ref="listEl" class="nfp-list">
                <li
                    v-for="finding in rows"
                    :key="finding.id"
                    class="nfp-row"
                    :class="{ 'nfp-row-flash': finding.id === flashedId }"
                    :data-finding-row="finding.id"
                >
                    <span class="nfp-row-title" :title="rowTitle(finding)">{{
                        rowTitle(finding)
                    }}</span>
                    <div class="nfp-row-tags">
                        <span v-for="tag in MOCK_TAGS" :key="tag.label" class="nfp-tag">
                            <span class="nfp-tag-dot" :style="{ background: tag.color }"></span>
                            <span class="nfp-tag-label">{{ tag.label }}</span>
                        </span>
                    </div>
                    <span class="nfp-row-count" :title="t('narratives.findingsPanel.linkedCount')">
                        <span class="material-icons-outlined nfp-row-count-icon">list</span>
                        {{ mockRefCount(finding) }}
                    </span>
                </li>
            </ul>
        </div>
    </aside>
</template>
